import React, { useMemo } from 'react';

import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Avatar, Button, Menu, MenuItem, css } from '@mui/material';
import { findFlagUrlByIso2Code } from 'country-flags-svg';

import { useLanguageSelect, withWidget } from '@ezbobdev/ui-commons';
import { useTranslation } from '@ezbobdev/ui-framework';

import { styled } from '../../Utils/style';

const Wrapper = styled('div')`
  ${(props) => {
    const { theme } = props;

    return `
      .MuiButton-root {
          text-transform: none;
      }

      .icon {
          ${theme.direction === 'rtl' ? 'margin-left: 8px;' : 'margin-right: 8px;'}
          width: 20px;
          height: 20px;
      }
    `;
  }}
`;

const ButtonStyled = styled(Button)`
  ${({ $isIconButton }) => css`
    ${
      $isIconButton &&
      `
        min-width: unset;
        padding: 0;
      `
    }

    & .icon {
      ${$isIconButton && 'margin: 0;'}
    }
  `}
`;

const LanguageSelect = (props) => {
  const { t } = useTranslation();
  const {
    useIconButtons,
    languageLabel,
    languageIso639Code,
    countryAlpha2Code,
    languageIconSrc,
    availableLanguages,
    menuAnchor,
    isMenuOpen,
    buttonProps,
    onLanguageChange,
    onMenuClose,
    onMenuOpen,
  } = useLanguageSelect(props);

  return useMemo(() => {
    const countryFlag = findFlagUrlByIso2Code(countryAlpha2Code);

    return (
      <Wrapper className={LanguageSelect.displayName} data-testid={LanguageSelect.displayName}>
        <ButtonStyled
          color={buttonProps?.color || 'inherit'}
          onClick={onMenuOpen}
          data-testid={`${LanguageSelect.displayName}-menuOpenButton`}
          aria-label={`${t('globals.languageSelect')} ${t(`globals.languages.${languageIso639Code}`)}`}
          $isIconButton={useIconButtons}
        >
          {countryFlag && (
            <Avatar
              variant="circular"
              className="icon"
              src={countryFlag}
              alt={`${countryAlpha2Code} ${t('globals.flag')}`}
            />
          )}
          {!useIconButtons && t(`globals.languages.${languageIso639Code}`)}
          {!useIconButtons && (isMenuOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />)}
        </ButtonStyled>

        <Menu
          anchorEl={menuAnchor}
          open={isMenuOpen}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          keepMounted
          onClose={onMenuClose}
        >
          {availableLanguages.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.selected}
              data-value={option.value}
              onClick={onLanguageChange}
              data-testid={`${LanguageSelect.displayName}-${option.value}`}
            >
              {t(`globals.languages.${option.value}`)}
            </MenuItem>
          ))}
        </Menu>
      </Wrapper>
    );
  }, [
    languageIconSrc,
    languageLabel,
    languageIso639Code,
    countryAlpha2Code,
    menuAnchor,
    isMenuOpen,
    availableLanguages,
  ]);
};

LanguageSelect.displayName = 'LanguageSelect';

export default React.memo(withWidget(LanguageSelect, { includeCaseMapProps: ['name'] }));
